import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PeopleIcon from '@mui/icons-material/People';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useAuth, getUserRole, ROLES, SUPER_ADMIN_EMAIL } from '../contexts/AuthContext';
import { apiFetch } from '../api/client';
import { withUnidade } from '../utils/storageByUnit';
import SubstitutosTab from '../components/gestao/SubstitutosTab';

/**
 * Central de Acessos — Gestão de Equipe.
 * Soft Delete only (nunca Hard Delete).
 * Listagem filtrada por unidadeSelecionada.codigo.
 */

const MOCK_USUARIOS = [
  {
    id: 'mock-1',
    nome: 'Ana Coordenadora',
    email: 'ana.coord@exemplo.com',
    tipo: 'coordenador',
    cargo: 'Coordenador',
    setor: 'Contratos',
    inativo: 0
  },
  {
    id: 'mock-2',
    nome: 'Bruno Fiscal',
    email: 'bruno.fiscal@exemplo.com',
    tipo: 'fiscal',
    cargo: 'Fiscal',
    setor: 'TI',
    inativo: 0
  },
  {
    id: 'mock-3',
    nome: 'Carla Analista',
    email: 'carla.analista@exemplo.com',
    tipo: 'analista',
    cargo: 'Analista',
    setor: 'Contratos',
    inativo: 1
  }
];

/** Opções de cargo → tipo persistido na API (sem duplicar identidade). */
const CARGO_OPTIONS = [
  { value: 'coordenador', label: 'Coordenador / Supervisor (LOCAL_ADMIN)', rbac: ROLES.LOCAL_ADMIN, corporativo: false },
  { value: 'coordenador_financeiro', label: 'Coordenador Financeiro (fiscal Financeiro + monitor)', rbac: ROLES.FISCAL_MONITOR, corporativo: false },
  { value: 'analista', label: 'Analista / Assistente / Auxiliar (LOCAL_OPERATOR)', rbac: ROLES.LOCAL_OPERATOR, corporativo: false },
  { value: 'analista_financeiro', label: 'Analista Financeiro (leitura ampla + ações financeiras)', rbac: ROLES.FINANCEIRO_ANALISTA, corporativo: false },
  { value: 'fiscal', label: 'Fiscal', rbac: ROLES.FISCAL, corporativo: false },
  { value: 'financeiro', label: 'Financeiro', rbac: ROLES.FINANCEIRO, corporativo: false },
  { value: 'usuario', label: 'Usuário do Setor (somente leitura)', rbac: ROLES.SECTOR_VIEWER, corporativo: false },
  { value: 'diretoria_executiva', label: 'Diretoria da Unidade (LOCAL_APPROVER)', rbac: ROLES.LOCAL_APPROVER, corporativo: false },
  { value: 'diretor_tecnico', label: 'Diretoria Técnica Corporativa', rbac: ROLES.GLOBAL_VIEWER, corporativo: true },
  { value: 'diretor_financeiro', label: 'Diretoria Financeira Corporativa', rbac: ROLES.GLOBAL_VIEWER, corporativo: true },
  { value: 'gerente_corporativo', label: 'Gerente Corporativo', rbac: ROLES.GLOBAL_VIEWER, corporativo: true },
];

/** Unidade lógica legada — novos corporativos são replicados em cada unidade real. */
export const UNIDADE_CORPORATIVA = 'CORPORATIVO';

const emptyForm = {
  nome: '',
  email: '',
  senha: '',
  cpf: '',
  tipo: 'analista',
  cargo: '',
  setor: '',
  unidade: ''
};

function formatCpfInput(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

function isCargoCorporativoOption(tipo) {
  return CARGO_OPTIONS.some((o) => o.value === tipo && o.corporativo);
}

function chipColorForRole(rbacRole) {
  if (rbacRole === ROLES.LOCAL_APPROVER || rbacRole === ROLES.GLOBAL_VIEWER || rbacRole === ROLES.SUPER_ADMIN) {
    return 'warning';
  }
  if (
    rbacRole === ROLES.LOCAL_ADMIN ||
    rbacRole === ROLES.LOCAL_OPERATOR ||
    rbacRole === ROLES.FINANCEIRO ||
    rbacRole === ROLES.FISCAL_MONITOR ||
    rbacRole === ROLES.FINANCEIRO_ANALISTA
  ) {
    return 'primary';
  }
  return 'default';
}

function formatLabel(s) {
  if (!s || typeof s !== 'string') return '—';
  return s.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

export default function GestaoUsuariosPage() {
  const {
    user,
    rbacRole,
    unidadeSelecionada,
    unidades,
    hasPermission
  } = useAuth();

  const [usuarios, setUsuarios] = useState([]);
  const [setores, setSetores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usingMock, setUsingMock] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmInativar, setConfirmInativar] = useState(null);
  const [modalNovoSetor, setModalNovoSetor] = useState(false);
  const [novoSetorNome, setNovoSetorNome] = useState('');
  const [confirmDeleteSetor, setConfirmDeleteSetor] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [tab, setTab] = useState(0);
  const [senhaModal, setSenhaModal] = useState(null);
  const [senhaForm, setSenhaForm] = useState({ senha: '', confirmar: '', gerarTemporaria: false });
  const [senhaSaving, setSenhaSaving] = useState(false);
  const [senhaTempResult, setSenhaTempResult] = useState(null);

  const isSuperAdmin = rbacRole === ROLES.SUPER_ADMIN;
  const podeRedefinirSenha = useMemo(() => {
    if (isSuperAdmin || rbacRole === ROLES.LOCAL_ADMIN) return true;
    const s = String(user?.setor || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    return s.includes('contrato');
  }, [isSuperAdmin, rbacRole, user]);
  const formEhCorporativo = isCargoCorporativoOption(form.tipo);

  const cargoOptions = useMemo(() => {
    if (isSuperAdmin) return CARGO_OPTIONS;
    return CARGO_OPTIONS.filter(
      (o) => o.rbac !== ROLES.LOCAL_APPROVER && o.rbac !== ROLES.GLOBAL_VIEWER
    );
  }, [isSuperAdmin]);

  const showSnack = (message, severity = 'success') => {
    setSnack({ open: true, message, severity });
  };

  const fetchSetores = useCallback(async () => {
    if (!unidadeSelecionada?.codigo) {
      setSetores([]);
      return;
    }
    try {
      const res = await apiFetch(withUnidade('/api/setores', unidadeSelecionada));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSetores(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn('[GestaoUsuarios] setores:', err?.message || err);
      setSetores([]);
    }
  }, [unidadeSelecionada]);

  const fetchUsuarios = useCallback(async () => {
    if (!unidadeSelecionada?.codigo) {
      setUsuarios([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const path = withUnidade('/api/usuarios', unidadeSelecionada);
      const res = await apiFetch(path);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setUsuarios(Array.isArray(data) ? data : []);
      setUsingMock(false);
    } catch (err) {
      console.warn('[GestaoUsuarios] API indisponível — usando mock temporário:', err?.message || err);
      setUsuarios(
        MOCK_USUARIOS.map((u) => ({
          ...u,
          unidade: unidadeSelecionada.codigo
        }))
      );
      setUsingMock(true);
      showSnack('API de usuários indisponível. Exibindo dados de demonstração.', 'warning');
    } finally {
      setLoading(false);
    }
  }, [unidadeSelecionada]);

  useEffect(() => {
    fetchUsuarios();
    fetchSetores();
  }, [fetchUsuarios, fetchSetores]);

  if (!hasPermission('GestaoUsuarios')) {
    return <Navigate to="/home" replace />;
  }

  const handleCriarSetor = async () => {
    const nome = novoSetorNome.trim();
    if (!nome) {
      showSnack('Informe o nome do setor.', 'error');
      return;
    }
    try {
      const res = await apiFetch(withUnidade('/api/setores', unidadeSelecionada), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao criar setor');
      }
      const created = await res.json();
      setSetores((prev) => [...prev, { id: created.id, nome: created.nome || nome }]);
      setForm((f) => ({ ...f, setor: created.nome || nome }));
      setModalNovoSetor(false);
      setNovoSetorNome('');
      showSnack('Setor criado com sucesso.');
    } catch (e) {
      showSnack(e.message || 'Erro ao criar setor.', 'error');
    }
  };

  const handleDeleteSetor = async () => {
    const setor = confirmDeleteSetor;
    if (!setor?.id) return;
    try {
      const res = await apiFetch(withUnidade(`/api/setores/${setor.id}`, unidadeSelecionada), {
        method: 'DELETE'
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao inativar setor');
      }
      setSetores((prev) => prev.filter((s) => s.id !== setor.id));
      if (form.setor === setor.nome) setForm((f) => ({ ...f, setor: '' }));
      setConfirmDeleteSetor(null);
      showSnack('Setor inativado (soft delete).');
    } catch (e) {
      showSnack(e.message || 'Erro ao remover setor.', 'error');
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...emptyForm,
      unidade: unidadeSelecionada?.codigo || '',
      tipo: 'analista'
    });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      nome: row.nome || '',
      email: row.email || '',
      senha: '',
      cpf: row.cpf || '',
      tipo: row.tipo || 'usuario',
      cargo: row.cargo || '',
      setor: row.setor || '',
      unidade: row.unidade || unidadeSelecionada?.codigo || ''
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.nome.trim() || !form.email.trim() || !form.tipo) {
      showSnack('Preencha nome, e-mail e cargo.', 'error');
      return;
    }
    if (!editing && !form.senha) {
      showSnack('Informe uma senha inicial para o novo usuário.', 'error');
      return;
    }
    const cpfDigits = String(form.cpf || '').replace(/\D/g, '');
    if (cpfDigits && cpfDigits.length !== 11) {
      showSnack('CPF inválido. Informe 11 dígitos ou deixe em branco.', 'error');
      return;
    }

    const emailNorm = form.email.trim().toLowerCase();
    if (emailNorm === SUPER_ADMIN_EMAIL && !isSuperAdmin) {
      showSnack('Não é permitido cadastrar o SUPER_ADMIN.', 'error');
      return;
    }

    const corporativo = isCargoCorporativoOption(form.tipo);
    const opt = CARGO_OPTIONS.find((o) => o.value === form.tipo);

    const payload = {
      nome: form.nome.trim(),
      email: emailNorm,
      cpf: cpfDigits || null,
      tipo: form.tipo,
      cargo: form.cargo || opt?.label || form.tipo,
      setor: form.setor || null,
      // Corporativos: backend replica o vínculo em TODAS as unidades ativas
      unidade: corporativo
        ? (unidadeSelecionada?.codigo || form.unidade)
        : (isSuperAdmin ? (form.unidade || unidadeSelecionada?.codigo) : unidadeSelecionada?.codigo)
    };

    setSaving(true);
    try {
      if (editing) {
        const res = await apiFetch(`/api/usuarios/${editing.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Erro ao atualizar');
        }
        showSnack('Usuário atualizado com sucesso.');
      } else {
        const res = await apiFetch('/api/usuarios', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, senha: form.senha })
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          if (usingMock || res.status === 404) {
            setUsuarios((prev) => [
              ...prev,
              {
                id: `mock-${Date.now()}`,
                ...payload,
                inativo: 0
              }
            ]);
            showSnack('Usuário adicionado (modo demonstração).', 'info');
            setModalOpen(false);
            setSaving(false);
            return;
          }
          throw new Error(err.error || 'Erro ao cadastrar');
        }
        showSnack(
          corporativo
            ? 'Usuário corporativo cadastrado — acesso a todas as unidades.'
            : 'Usuário cadastrado com sucesso.'
        );
      }
      setModalOpen(false);
      await fetchUsuarios();
    } catch (e) {
      if (usingMock && editing) {
        setUsuarios((prev) =>
          prev.map((u) => (u.id === editing.id ? { ...u, ...payload } : u))
        );
        showSnack('Usuário atualizado (modo demonstração).', 'info');
        setModalOpen(false);
      } else if (usingMock && !editing) {
        setUsuarios((prev) => [
          ...prev,
          { id: `mock-${Date.now()}`, ...payload, inativo: 0 }
        ]);
        showSnack('Usuário adicionado (modo demonstração).', 'info');
        setModalOpen(false);
      } else {
        showSnack(e.message || 'Erro ao salvar usuário.', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  /** Soft Delete — nunca chama DELETE hard. */
  const handleInativar = async () => {
    if (!confirmInativar) return;
    const alvo = confirmInativar;
    setSaving(true);
    try {
      let res = await apiFetch(`/api/usuarios/${alvo.id}/inativar`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inativo: true })
      });
      if (!res.ok) {
        // Fallback: PUT com flag inativo (quando a rota PATCH ainda não existir)
        res = await apiFetch(`/api/usuarios/${alvo.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            nome: alvo.nome,
            email: alvo.email,
            tipo: alvo.tipo,
            cargo: alvo.cargo,
            setor: alvo.setor,
            unidade: alvo.unidade || unidadeSelecionada?.codigo,
            inativo: true
          })
        });
      }
      if (!res.ok && !usingMock) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Não foi possível inativar');
      }
      setUsuarios((prev) =>
        prev.map((u) => (u.id === alvo.id ? { ...u, inativo: 1 } : u))
      );
      showSnack('Usuário inativado (soft delete).');
      setConfirmInativar(null);
      if (!usingMock) await fetchUsuarios();
    } catch (e) {
      if (usingMock || String(alvo.id).startsWith('mock')) {
        setUsuarios((prev) =>
          prev.map((u) => (u.id === alvo.id ? { ...u, inativo: 1 } : u))
        );
        showSnack('Usuário inativado (modo demonstração).', 'info');
        setConfirmInativar(null);
      } else {
        showSnack(e.message || 'Erro ao inativar.', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 0 } }}>
      <Card
        sx={{
          mb: 3,
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(227,242,253,0.9) 100%)'
        }}
      >
        <CardContent>
          <Box
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
          >
            <Box display="flex" alignItems="flex-start" gap={1.5}>
              <PeopleIcon color="primary" sx={{ mt: 0.5, fontSize: 32 }} />
              <Box>
                <Typography variant="h5" fontWeight={700} color="primary.main">
                  Gestão de Equipe e Acessos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Gerenciando acessos para:{' '}
                  <strong>{unidadeSelecionada?.nome || '—'}</strong>
                </Typography>
                {usingMock && (
                  <Chip label="Modo demonstração" size="small" color="warning" sx={{ mt: 1 }} />
                )}
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{ borderRadius: 2, fontWeight: 600, px: 2.5 }}
              disabled={tab !== 0}
            >
              Adicionar Novo Usuário
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Colaboradores" />
        <Tab label="Substitutos" />
      </Tabs>

      {tab === 1 ? (
        <Paper sx={{ p: 2, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
          <SubstitutosTab
            unidadeSelecionada={unidadeSelecionada}
            usuarios={usuarios}
            onNotify={showSnack}
          />
        </Paper>
      ) : (
      <TableContainer
        component={Paper}
        sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" p={6}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                <TableCell sx={{ fontWeight: 700 }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>E-mail</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Cargo</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usuarios.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Nenhum usuário nesta unidade.
                  </TableCell>
                </TableRow>
              ) : (
                usuarios.map((row) => {
                  const role = getUserRole(row) || ROLES.SECTOR_VIEWER;
                  const inativo = row.inativo === 1 || row.inativo === true;
                  return (
                    <TableRow key={row.id} hover sx={{ opacity: inativo ? 0.65 : 1 }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{row.nome}</Typography>
                        {row.setor && (
                          <Typography variant="caption" color="text.secondary">{row.setor}</Typography>
                        )}
                      </TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={formatLabel(row.cargo || row.tipo || role)}
                          color={chipColorForRole(role)}
                          sx={{ fontWeight: 600 }}
                        />
                        {(row.unidade === 'CORPORATIVO' || role === ROLES.GLOBAL_VIEWER || isCargoCorporativoOption(row.tipo)) && (
                          <Chip size="small" label="Todas unidades" variant="outlined" color="warning" sx={{ ml: 0.5, fontWeight: 600 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={inativo ? 'Inativo' : 'Ativo'}
                          color={inativo ? 'default' : 'success'}
                          variant={inativo ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Editar">
                          <IconButton color="primary" onClick={() => openEdit(row)} size="small">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        {podeRedefinirSenha && !inativo && (
                          <Tooltip title="Redefinir senha">
                            <IconButton
                              color="warning"
                              size="small"
                              onClick={() => {
                                setSenhaTempResult(null);
                                setSenhaForm({ senha: '', confirmar: '', gerarTemporaria: false });
                                setSenhaModal(row);
                              }}
                            >
                              <LockResetIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Inativar (soft delete)">
                          <span>
                            <IconButton
                              color="error"
                              onClick={() => setConfirmInativar(row)}
                              size="small"
                              disabled={inativo || (row.email || '').toLowerCase() === SUPER_ADMIN_EMAIL}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      )}

      {/* Modal Adicionar / Editar */}
      <Dialog open={modalOpen} onClose={() => !saving && setModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? 'Editar Usuário' : 'Adicionar Novo Usuário'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                label="Nome Completo"
                fullWidth
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="E-mail (login)"
                type="email"
                fullWidth
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                disabled={!!editing}
                autoComplete="off"
                inputProps={{ autoComplete: 'new-password' }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="CPF"
                fullWidth
                value={form.cpf}
                onChange={(e) => setForm({ ...form, cpf: formatCpfInput(e.target.value) })}
                placeholder="000.000.000-00"
                helperText="Usado em Assinaturas e Termo de Designação do fiscal/suplente."
                inputProps={{ inputMode: 'numeric' }}
              />
            </Grid>
            {!editing && (
              <Grid item xs={12}>
                <TextField
                  label="Senha inicial"
                  type="password"
                  fullWidth
                  value={form.senha}
                  onChange={(e) => setForm({ ...form, senha: e.target.value })}
                  required
                  autoComplete="new-password"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Cargo</InputLabel>
                <Select
                  label="Cargo"
                  value={form.tipo}
                  onChange={(e) => {
                    const opt = CARGO_OPTIONS.find((o) => o.value === e.target.value);
                    const tipoVal = e.target.value;
                    const ehFinanceiroCargo =
                      tipoVal === 'coordenador_financeiro' || tipoVal === 'analista_financeiro';
                    const setorFinanceiro = setores.find(
                      (s) =>
                        String(s.nome || '')
                          .normalize('NFD')
                          .replace(/[\u0300-\u036f]/g, '')
                          .toLowerCase()
                          .includes('financeir')
                    );
                    setForm({
                      ...form,
                      tipo: tipoVal,
                      cargo: opt?.label || tipoVal,
                      unidade: unidadeSelecionada?.codigo || form.unidade,
                      ...(ehFinanceiroCargo && setorFinanceiro?.nome && !form.setor
                        ? { setor: setorFinanceiro.nome }
                        : {}),
                    });
                  }}
                >
                  {cargoOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {!formEhCorporativo && (
              <Grid item xs={12}>
                <Box display="flex" gap={1} alignItems="flex-start">
                  <FormControl fullWidth>
                    <InputLabel>Setor</InputLabel>
                    <Select
                      label="Setor"
                      value={form.setor || ''}
                      onChange={(e) => setForm({ ...form, setor: e.target.value })}
                    >
                      <MenuItem value="">
                        <em>Nenhum</em>
                      </MenuItem>
                      {setores.map((s) => (
                        <MenuItem key={s.id} value={s.nome}>{s.nome}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Tooltip title="Criar novo setor">
                    <IconButton
                      color="primary"
                      onClick={() => setModalNovoSetor(true)}
                      sx={{ mt: 0.5, border: '1px solid', borderColor: 'primary.main', borderRadius: 2 }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Inativar setor selecionado">
                    <span>
                      <IconButton
                        color="error"
                        disabled={!form.setor || !setores.some((s) => s.nome === form.setor)}
                        onClick={() => {
                          const s = setores.find((x) => x.nome === form.setor);
                          if (s) setConfirmDeleteSetor(s);
                        }}
                        sx={{ mt: 0.5, border: '1px solid', borderColor: 'error.main', borderRadius: 2 }}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
            )}
            <Grid item xs={12}>
              {formEhCorporativo ? (
                <TextField
                  label="Unidade"
                  fullWidth
                  value="Todas as unidades ativas"
                  disabled
                  helperText="O cadastro corporativo é replicado automaticamente em CEAP-SOL, HRLN, HRJR, etc. Os dados do sistema continuam separados por unidade."
                />
              ) : isSuperAdmin ? (
                <FormControl fullWidth>
                  <InputLabel>Unidade</InputLabel>
                  <Select
                    label="Unidade"
                    value={form.unidade || unidadeSelecionada?.codigo || ''}
                    onChange={(e) => setForm({ ...form, unidade: e.target.value })}
                  >
                    {(unidades || []).map((u) => (
                      <MenuItem key={u.id || u.codigo} value={u.codigo}>{u.nome}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  label="Unidade"
                  fullWidth
                  value={unidadeSelecionada?.nome || ''}
                  disabled
                />
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ borderRadius: 2 }}>
            {saving ? <CircularProgress size={22} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={modalNovoSetor} onClose={() => setModalNovoSetor(false)} fullWidth maxWidth="xs">
        <DialogTitle>Novo Setor</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nome do setor"
            value={novoSetorNome}
            onChange={(e) => setNovoSetorNome(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalNovoSetor(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCriarSetor}>Criar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDeleteSetor} onClose={() => setConfirmDeleteSetor(null)}>
        <DialogTitle>Inativar setor?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            O setor <strong>{confirmDeleteSetor?.nome}</strong> será inativado (soft delete) nesta unidade.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteSetor(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDeleteSetor}>Inativar</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmar soft delete usuário */}
      <Dialog open={!!confirmInativar} onClose={() => !saving && setConfirmInativar(null)}>
        <DialogTitle>Inativar usuário?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            O usuário <strong>{confirmInativar?.nome}</strong> será marcado como inativo (soft delete).
            Nenhum registro será apagado do banco.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmInativar(null)} disabled={saving}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleInativar} disabled={saving}>
            Inativar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(senhaModal)}
        onClose={() => !senhaSaving && setSenhaModal(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Redefinir senha</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Usuário: <strong>{senhaModal?.nome}</strong> ({senhaModal?.email})
          </Typography>
          {senhaTempResult ? (
            <Alert severity="warning">
              Senha temporária (copie agora — não será exibida novamente):
              <br />
              <strong style={{ fontSize: 16 }}>{senhaTempResult}</strong>
            </Alert>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={senhaSaving}
                  onClick={async () => {
                    setSenhaSaving(true);
                    try {
                      const res = await apiFetch(
                        withUnidade(`/api/usuarios/${senhaModal.id}/redefinir-senha`, unidadeSelecionada),
                        {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ gerarTemporaria: true }),
                        }
                      );
                      const data = await res.json().catch(() => ({}));
                      if (!res.ok) throw new Error(data.error || 'Falha ao redefinir');
                      setSenhaTempResult(data.senhaTemporaria || null);
                      showSnack('Senha temporária gerada. Sessões invalidadas.', 'success');
                    } catch (e) {
                      showSnack(e.message, 'error');
                    } finally {
                      setSenhaSaving(false);
                    }
                  }}
                >
                  Gerar senha temporária
                </Button>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label="Nova senha"
                  value={senhaForm.senha}
                  onChange={(e) => setSenhaForm((f) => ({ ...f, senha: e.target.value }))}
                  autoComplete="new-password"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label="Confirmar senha"
                  value={senhaForm.confirmar}
                  onChange={(e) => setSenhaForm((f) => ({ ...f, confirmar: e.target.value }))}
                  autoComplete="new-password"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSenhaModal(null)} disabled={senhaSaving}>
            Fechar
          </Button>
          {!senhaTempResult && (
            <Button
              variant="contained"
              disabled={senhaSaving}
              onClick={async () => {
                if (senhaForm.senha.length < 6) {
                  showSnack('Senha deve ter ao menos 6 caracteres.', 'error');
                  return;
                }
                if (senhaForm.senha !== senhaForm.confirmar) {
                  showSnack('Confirmação de senha não confere.', 'error');
                  return;
                }
                setSenhaSaving(true);
                try {
                  const res = await apiFetch(
                    withUnidade(`/api/usuarios/${senhaModal.id}/redefinir-senha`, unidadeSelecionada),
                    {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ senha: senhaForm.senha }),
                    }
                  );
                  const data = await res.json().catch(() => ({}));
                  if (!res.ok) throw new Error(data.error || 'Falha ao redefinir');
                  showSnack('Senha redefinida. Sessões invalidadas.', 'success');
                  setSenhaModal(null);
                } catch (e) {
                  showSnack(e.message, 'error');
                } finally {
                  setSenhaSaving(false);
                }
              }}
            >
              Salvar nova senha
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
